import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, GridReadyEvent, GridApi } from 'ag-grid-enterprise';
import { SpaceBetween, Alert, Popover } from '@amzn/awsui-components-core';
import { colorTextStatusSuccess, colorTextStatusWarning, colorTextStatusError, colorTextBodySecondary } from '@amzn/awsui-design-tokens-core/index';
import { Avatar, AvatarSize } from '@amzn/stencil-react-components/avatar';
import { EmployeePopoverCard } from '../ea-huddle-v2/components/attendees/EmployeePopoverCard';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { ConnectionsScorecard, CONNECTIONS_THEMES, THEME_DISPLAY_NAMES, RangeIndicator } from './types';
import { LeaderDirectsDetailRenderer } from './LeaderDirectsDetailRenderer';

interface LeaderScorecardGridProps {
  scorecards: ConnectionsScorecard[];
  enableDrillDown?: boolean;
  startDate?: string;
  metricPeriod?: string;
  compact?: boolean;
}

// Privacy threshold — small samples render as muted with "*" suffix
const SMALL_SAMPLE_THRESHOLD = 20;

const RANGE_COLORS: Record<NonNullable<RangeIndicator>, string> = {
  above: colorTextStatusSuccess,
  within: colorTextStatusWarning,
  below: colorTextStatusError
};

const RANGE_ARROWS: Record<NonNullable<RangeIndicator>, string> = {
  above: '\u25B2', // ▲
  within: '\u25CF', // ●
  below: '\u25BC' // ▼
};

interface ScoreCellValue {
  metricValue: number | null;
  rangeIndicator: RangeIndicator;
  sampleSize: number | null;
  populationSize: number | null;
  missingValueReason: string | null;
  benchmarkMean: number | null;
  benchmarkMin: number | null;
  benchmarkMax: number | null;
}

const ScoreCellRenderer: React.FC<ICellRendererParams<unknown, ScoreCellValue>> = ({ value }) => {
  if (!value) return null;
  const { metricValue, rangeIndicator, sampleSize, missingValueReason } = value;

  if (metricValue == null) {
    return (
      <span title={missingValueReason || 'Insufficient data'} style={{ color: colorTextBodySecondary, fontStyle: 'italic' }}>
        n/a
      </span>
    );
  }

  const color = rangeIndicator ? RANGE_COLORS[rangeIndicator] : colorTextBodySecondary;
  const arrow = rangeIndicator ? RANGE_ARROWS[rangeIndicator] : '';
  const isSmall = sampleSize != null && sampleSize < SMALL_SAMPLE_THRESHOLD;
  const pct = (metricValue * 100).toFixed(1);

  const fmtPct = (n: number | null) => (n != null ? `${(n * 100).toFixed(1)}%` : '?');
  const sample = value.sampleSize;
  const population = value.populationSize;
  const responseRate = sample != null && population != null && population > 0 ? `${((sample / population) * 100).toFixed(1)}%` : 'n/a';

  const detailRow = (label: string, val: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', fontSize: '13px' }}>
      <span style={{ color: colorTextBodySecondary }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{val}</span>
    </div>
  );

  const popoverContent = (
    <div style={{ minWidth: '220px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {detailRow('Score', `${pct}%`)}
      {detailRow('Expected range', `${fmtPct(value.benchmarkMin)} - ${fmtPct(value.benchmarkMax)}`)}
      {detailRow('Expected mean', fmtPct(value.benchmarkMean))}
      {detailRow('Sample (respondents)', sample != null ? sample.toLocaleString() : 'n/a')}
      {detailRow('Population (eligible)', population != null ? population.toLocaleString() : 'n/a')}
      {detailRow('Response rate', responseRate)}
      {isSmall && (
        <div style={{ fontSize: '11px', color: colorTextStatusWarning, marginTop: '4px' }}>
          * Sample below {SMALL_SAMPLE_THRESHOLD} - interpret directionally.
        </div>
      )}
    </div>
  );

  return (
    <Popover header="Theme detail" position="top" size="medium" triggerType="custom" dismissButton renderWithPortal content={popoverContent}>
      <span style={{ color, fontWeight: 600, cursor: 'pointer', borderBottom: '1px dotted currentColor' }}>
        {arrow} {pct}%{isSmall ? '*' : ''}
      </span>
    </Popover>
  );
};

/**
 * Cell renderer for the Leader column. Shows a Stencil Avatar (photo + name + alias link)
 * wrapped in EmployeePopoverCard so clicking the avatar opens the badge popover with
 * job title, level, department, location. Same pattern as Kingpin's OwnersCellRenderer.
 */
const LeaderCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as { leader?: string; leaderLogin?: string; hasDirectOrgBreakdown?: boolean } | undefined;
  const alias = data?.leaderLogin;
  const display = data?.leader || alias || '-';
  // AG Grid v35 applies `ag-row-group-leaf-indent` to non-master rows in drill-down grids,
  // which natively aligns leaf rows with their chevron-bearing siblings. No custom padding needed.
  const inline: React.CSSProperties = { display: 'inline-block' };

  if (!alias) {
    return <span style={inline}>{display}</span>;
  }

  const avatar = <Avatar username={alias} fullName={display} size={AvatarSize.Small} imageAltText={`Profile image of ${display}`} />;

  return (
    <span style={inline}>
      <EmployeePopoverCard loginName={alias} renderWithPortal>
        <span style={{ cursor: 'pointer' }}>{avatar}</span>
      </EmployeePopoverCard>
    </span>
  );
};

export const LeaderScorecardGrid: React.FC<LeaderScorecardGridProps> = ({
  scorecards,
  enableDrillDown = false,
  startDate,
  metricPeriod,
  compact = false
}) => {
  const gridApiRef = useRef<GridApi | null>(null);
  const { agGridTheme } = useTheme();

  // Pivot themes into columns for AG Grid
  const rowData = useMemo(() => {
    return scorecards.map((sc) => {
      const themeMap = Object.fromEntries(sc.themes.map((t) => [t.theme.toUpperCase(), t]));
      const row: Record<string, unknown> = {
        leader: sc.displayName || sc.leaderLogin,
        leaderLogin: sc.leaderLogin,
        metricPeriod: sc.metricPeriod,
        hasDirectOrgBreakdown: sc.hasDirectOrgBreakdown
      };
      // Aggregated population/sample (max across themes — typically the same value)
      const allSamples = sc.themes.map((t) => t.sampleSize).filter((s): s is number => s != null);
      const allPops = sc.themes.map((t) => t.populationSize).filter((s): s is number => s != null);
      row.sampleSize = allSamples.length ? Math.max(...allSamples) : null;
      row.populationSize = allPops.length ? Math.max(...allPops) : null;
      row.responseRate =
        row.sampleSize != null && row.populationSize != null && (row.populationSize as number) > 0
          ? ((row.sampleSize as number) / (row.populationSize as number)) * 100
          : null;
      // One value per theme
      CONNECTIONS_THEMES.forEach((theme) => {
        const t = themeMap[theme];
        row[theme] = t
          ? {
              metricValue: t.metricValue,
              rangeIndicator: t.rangeIndicator,
              sampleSize: t.sampleSize,
              populationSize: t.populationSize,
              missingValueReason: t.missingValueReason,
              benchmarkMean: t.benchmarkMean,
              benchmarkMin: t.benchmarkMin,
              benchmarkMax: t.benchmarkMax
            }
          : {
              metricValue: null,
              rangeIndicator: null,
              sampleSize: null,
              populationSize: null,
              missingValueReason: null,
              benchmarkMean: null,
              benchmarkMin: null,
              benchmarkMax: null
            };
      });
      return row;
    });
  }, [scorecards]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'leader',
        headerName: 'Leader',
        pinned: 'left',
        minWidth: 280,
        cellRenderer: enableDrillDown ? 'agGroupCellRenderer' : LeaderCellRenderer,
        cellRendererParams: enableDrillDown ? { innerRenderer: LeaderCellRenderer } : undefined
      },
      {
        field: 'populationSize',
        headerName: 'Population (max across themes)',
        headerTooltip:
          'Largest eligible-audience count across the 4 themes for this leader. Typically identical across themes since Connections surveys all themes to the same audience.',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 130,
        valueFormatter: (params: { value: number | null }) => (params.value != null ? params.value.toLocaleString() : '-')
      },
      {
        field: 'responseRate',
        headerName: 'Response Rate (max across themes)',
        headerTooltip:
          'Response rate derived from the largest sample/population across the 4 themes for this leader. Per-theme values are in each theme cell (click to view).',
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 150,
        valueFormatter: (params: { value: number | null }) => (params.value != null ? `${params.value.toFixed(1)}%` : '-')
      },
      ...CONNECTIONS_THEMES.map((theme) => ({
        field: theme,
        headerName: THEME_DISPLAY_NAMES[theme],
        minWidth: 160,
        cellRenderer: ScoreCellRenderer,
        valueFormatter: (params: { value: ScoreCellValue | null }) => {
          // Plain-text formatter for display, sorting and clipboard copy
          if (!params.value || params.value.metricValue == null) return 'n/a';
          return `${(params.value.metricValue * 100).toFixed(1)}%`;
        },
        comparator: (a: ScoreCellValue | null, b: ScoreCellValue | null) => {
          const av = a?.metricValue ?? -1;
          const bv = b?.metricValue ?? -1;
          return av - bv;
        }
      }))
    ],
    [enableDrillDown]
  );

  const autoSizeAll = useCallback(() => {
    gridApiRef.current?.autoSizeAllColumns();
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApiRef.current = params.api;
      setTimeout(autoSizeAll, 100);
    },
    [autoSizeAll]
  );

  useEffect(() => {
    const timer = setTimeout(autoSizeAll, 100);
    return () => clearTimeout(timer);
  }, [rowData, autoSizeAll]);

  const hasSmallSample = useMemo(
    () =>
      rowData.some((r) =>
        CONNECTIONS_THEMES.some((theme) => {
          const t = r[theme] as ScoreCellValue;
          return t?.sampleSize != null && t.sampleSize < SMALL_SAMPLE_THRESHOLD;
        })
      ),
    [rowData]
  );

  return (
    <SpaceBetween size="m">
      {!compact && hasSmallSample && (
        <Alert type="info" header="Small samples flagged with *">
          Cells marked with <strong>*</strong> have a sample size smaller than {SMALL_SAMPLE_THRESHOLD}. Interpret directionally.
        </Alert>
      )}
      <div style={{ width: '100%' }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{ sortable: true, resizable: true }}
          tooltipShowDelay={300}
          domLayout="autoHeight"
          onGridReady={onGridReady}
          theme={agGridTheme}
          masterDetail={enableDrillDown}
          isRowMaster={enableDrillDown ? (data: any) => data?.hasDirectOrgBreakdown === true : undefined}
          detailCellRenderer={enableDrillDown ? LeaderDirectsDetailRenderer : undefined}
          detailRowAutoHeight={enableDrillDown}
          context={{ startDate, metricPeriod }}
        />
      </div>
    </SpaceBetween>
  );
};
